import * as Yup from "yup";
import {
	Formik,
	Form,
	FormikProps,
	setNestedObjectValues,
	FormikTouched,
	useFormikContext,
} from "formik";
import {
	FormStates,
	SetupAssistantForm,
} from "./setupAssistant.types";
import { MachineProps } from "./setupAssistantMachine.types";
import { useMemo, useEffect } from "react";
import { validatePositiveNumber } from "@pages/activityBuilder/modules/moduleUtils";
import { AllPossibleSetupAssistantForm } from "./setupAssistant.types";
import { useClimateConfigurationValidation } from "@pages/project/description/projectDescriptionUtils";
import SetupAssistantButton from "@components/button/SetupAssistantButton";
import SetupAssistantStep from "./SetupAssistantStep";
import { useAppSelector } from "@app/hooks";
import { selectCurrentProject } from "@features/project/projectSlice";
import ActivityDefaultSocSubmodule from "../../../activity/ActivityDefaultSocSubmodule";

// ValidationHandler component to handle climate configuration validation within Formik context
const ValidationHandler = () => {
	const { values, setFieldError } = useFormikContext<Partial<AllPossibleSetupAssistantForm>>();
	const { project } = useAppSelector(selectCurrentProject);
	const { isValid, shouldValidate } = useClimateConfigurationValidation(
		values.climate ?? null,
		values.moisture ?? null,
		project?.soil_type?.id ?? null
	);

	useEffect(() => {
		if (shouldValidate && !isValid) {
			setFieldError('climate', 'validations.invalid_soc_configuration');
			setFieldError('moisture', 'validations.invalid_soc_configuration');
		} else {
			setFieldError('climate', undefined);
			setFieldError('moisture', undefined);
		}
	}, [isValid, shouldValidate, setFieldError]);

	return null; // This component doesn't render anything
};

const SetupAssistantWizard = ({
	handleNext,
	machineProps,
}: {
	handleNext: (values: SetupAssistantForm) => void;
	machineProps: MachineProps;
}) => {
	const {
		schema,
		currentState,
		values,
		canBack,
		canSkip,
		skip,
		goBack,
		isFinal,
	} = machineProps;

	const withoutLandUseValidationSchema = useMemo(
		() =>
			Yup.object({
				areaWithoutProject: validatePositiveNumber.required(),
				withoutProjectLandUse: validatePositiveNumber
					.when("areaWithoutProject", {
						is: (areaWithoutProject: number) => areaWithoutProject === 1,
						then: validatePositiveNumber.required(),
					})
					.test({
						name: "not-equal",
						message: "validations.luc_not_equal",
						test: function (value) {
							const areaWithoutProject = this.parent.areaWithoutProject;
							return (
								areaWithoutProject === 0 || value !== values.previousLandUse
							);
						},
					}),
			}),
		[values.previousLandUse],
	);

	return (
		<>
			{schema.map((submodule, i) => (
				<Formik
					initialValues={{ ...submodule?.initialValues, ...values }}
					validationSchema={
						submodule.step === FormStates.areaWithoutProject
							? withoutLandUseValidationSchema
							: submodule?.validationSchema
					}
					onSubmit={handleNext}
					enableReinitialize
					key={submodule.step}
				>
					{({
						validateForm,
						setTouched,
						values: stepValues,
					}: FormikProps<Partial<AllPossibleSetupAssistantForm>>) => (
						<Form>
							{/* Add ValidationHandler inside the Formik context */}
							<ValidationHandler />
							
							<SetupAssistantStep
								step={submodule}
								isComplete={currentState !== submodule.step}
								index={i}
								extraContent={
									submodule.step === FormStates.initInfo ? (
										<ActivityDefaultSocSubmodule
											climateField="climate"
											moistureField="moisture"
											socT2Field="soc_t2"
											disabled={currentState !== submodule.step}
										/>
									) : null
								}
								extraContentAfterInputName="moisture"
							>
								{currentState === submodule.step ? (
									<div
										className="py-2 ps-1 d-flex align-items-center"
										style={{ gap: 8 }}
									>
										{canBack ? (
											<SetupAssistantButton
												handleClick={() => goBack(submodule?.initialValues)}
												type="back"
											/>
										) : null}
										{canSkip ? (
											<SetupAssistantButton
												type="skip"
												handleClick={async () => {
													const errors = await validateForm();
													if (Object.keys(errors).length === 0) {
														skip(stepValues as SetupAssistantForm);
													} else {
														setTouched(
															setNestedObjectValues<FormikTouched<unknown>>(
																errors,
																true,
															),
														);
													}
												}}
											/>
										) : null}
										{!isFinal ? <SetupAssistantButton type="next" /> : null}
									</div>
								) : null}
							</SetupAssistantStep>
						</Form>
					)}
				</Formik>
			))}
		</>
	);
};

export default SetupAssistantWizard;
